import './styles/graphic.scss';

import rawData from './data/data.json';
import kushnerImg from './img/kushner-sm.png';

const STAT_HEADLINE = 'Phoenix invests at least $456M across nine companies involved in Israel’s war machine';
const BOX_FILL = '#ececec';
const PERSON_BG = '#d9d9d9';
const CATEGORY_COLORS = ['#53A796', '#5194C3', '#F8C153', '#F18851', '#7C4EA5', '#C52622'];
const OTHER_COLOR = '#b1b1b1';

// one hue per company, with gray held back for the grouped "other" span so
// it doesn't read as just another company
function buildPalette(segments) {
  return segments.map((d, i) => (
    d.isOther ? OTHER_COLOR : CATEGORY_COLORS[i % CATEGORY_COLORS.length]
  ));
}

// `data.json` is a raw pass-through of the sheet's "data" tab (refreshed by
// config.devServer.js on every page load), so it has no category column, and a
// company split across exchanges arrives as one row per listing. Categories are
// assigned here until/unless a "Category" column is added to the sheet itself.
const CATEGORY_MAP = {
  'Elbit Systems': 'Munitions and electronics',
  'Reshef Technologies': 'Munitions and electronics',
  'Next Vision': 'Drones, cameras and optics',
  'RP Optical Lab': 'Drones, cameras and optics',
  // the sheet has spelled this both ways; an unmapped name drops out of the
  // grid entirely, so keep both
  'Beit Shemesh Engines': 'Aircraft and engines',
  'Bet Shemesh Engines': 'Aircraft and engines',
  Boeing: 'Aircraft and engines',
  'Ashot Ashkelon': 'Tanks and bulldozers',
  Caterpillar: 'Tanks and bulldozers',
  'Israel Shipyards Ltd': 'Warships',
};

const CATEGORY_ORDER = [
  'Munitions and electronics',
  'Drones, cameras and optics',
  'Aircraft and engines',
  'Tanks and bulldozers',
  'Warships',
];

// Shortened for the narrow bar-chart columns only; full names still appear
// in the category grid below.
const BAR_LABEL_OVERRIDES = {
  'Reshef Technologies': 'Reshef Tech.',
};

function cleanCompanyName(name) {
  return name.replace(/\s*\(.*\)\s*$/, '').trim();
}

function getUsdValue(row) {
  const key = Object.keys(row).find(k => /^usd value/i.test(k));
  return Number(row[key]) || 0;
}

function transformData(raw) {
  const merged = new Map();

  raw.forEach((row) => {
    const company = cleanCompanyName(row.Company);
    const value = getUsdValue(row);
    const description = (row.Description || '').trim();

    if (merged.has(company)) {
      merged.get(company).value += value;
    } else {
      merged.set(company, {
        company,
        value,
        description,
        category: CATEGORY_MAP[company] || 'Other',
      });
    }
  });

  return Array.from(merged.values());
}

const data = transformData(rawData);

function formatDollars(value) {
  if (value >= 1000000) {
    const millions = Math.round(Math.round((value / 1000000) * 100) / 10) / 10;
    return `$${millions}M`;
  }
  const thousands = Math.round(value / 1000);
  return `$${thousands}K`;
}

function buildSegments(topN = 4, otherLabel = 'Other') {
  const sorted = [...data].sort((a, b) => b.value - a.value);
  const top = sorted.slice(0, topN);
  const rest = sorted.slice(topN);
  const named = top.map(d => ({ label: d.company, value: d.value }));

  if (rest.length === 0) return named;

  const otherValue = rest.reduce((sum, d) => sum + d.value, 0);
  return [...named, { label: otherLabel, value: otherValue, isOther: true }];
}

function drawFlowDiagram(wrap, width) {
  const H = 160;
  const personR = 40;
  const personCX = personR + 20;
  const personCY = 50;
  const boxW = Math.max(140, Math.min(200, width * 0.2));
  const boxH = 46;
  const boxY = 26;
  const returnY = H - 34;

  // the right-side nub on the return path needs a little room past box2,
  // so the whole row (person -> arrow -> box -> arrow -> box) stretches to
  // fill the graphic's full width rather than leaving dead space on the right.
  const rightMargin = 26;
  const arrow1Start = personCX + personR + 10;
  const usableWidth = (width - rightMargin) - arrow1Start;
  const arrowLen = Math.max(90, (usableWidth - boxW * 2) / 2);
  const box1X = arrow1Start + arrowLen;
  const arrow2Start = box1X + boxW + 8;
  const box2X = arrow2Start + arrowLen;

  const svg = wrap.append('svg')
    .attr('class', 'flow-diagram')
    .attr('viewBox', `0 0 ${width} ${H}`)
    .attr('width', '100%')
    .attr('height', H);

  const defs = svg.append('defs');
  defs.append('marker')
    .attr('id', 'arrowhead')
    .attr('viewBox', '0 0 10 10')
    .attr('refX', 8)
    .attr('refY', 5)
    .attr('markerWidth', 7)
    .attr('markerHeight', 7)
    .attr('orient', 'auto-start-reverse')
    .append('path')
    .attr('d', 'M 0 0 L 10 5 L 0 10 z')
    .attr('fill', '#b1b1b1');

  // person photo (rendered smaller than the background circle, leaving a
  // visible ring, and clipped to its own circle so it stays perfectly round)
  const photoR = personR - 2;
  const clipId = 'kushner-clip';
  defs.append('clipPath')
    .attr('id', clipId)
    .append('circle')
    .attr('cx', personCX)
    .attr('cy', personCY)
    .attr('r', photoR);

  svg.append('circle')
    .attr('class', 'person-bg')
    .attr('cx', personCX)
    .attr('cy', personCY)
    .attr('r', personR)
    .attr('fill', PERSON_BG);

  svg.append('image')
    .attr('href', kushnerImg)
    .attr('x', personCX - photoR)
    .attr('y', personCY - photoR)
    .attr('width', photoR * 2)
    .attr('height', photoR * 2)
    .attr('clip-path', `url(#${clipId})`)
    .attr('preserveAspectRatio', 'xMidYMid slice');

  svg.append('text')
    .attr('class', 'person-name')
    .attr('x', personCX)
    .attr('y', personCY + personR + 20)
    .attr('text-anchor', 'middle')
    .text('Jared Kushner');

  // arrow 1: person -> Affinity Partners (starts flush against the photo
  // circle; only the end stops short, to leave room for the arrowhead)
  const line1Start = personCX + personR;
  const arrow1End = box1X - 8;
  svg.append('line')
    .attr('class', 'connector')
    .attr('x1', line1Start)
    .attr('y1', personCY)
    .attr('x2', arrow1End)
    .attr('y2', personCY)
    .attr('marker-end', 'url(#arrowhead)');

  const labelGap = 12; // consistent distance between label text and its arrow
  svg.append('text')
    .attr('class', 'connector-label')
    .attr('x', (line1Start + arrow1End) / 2)
    .attr('y', personCY - labelGap)
    .attr('text-anchor', 'middle')
    .text('Founded...');

  // box: Affinity Partners
  svg.append('rect')
    .attr('class', 'flow-box')
    .attr('x', box1X)
    .attr('y', boxY)
    .attr('width', boxW)
    .attr('height', boxH)
    .attr('rx', 6)
    .attr('fill', BOX_FILL);

  svg.append('text')
    .attr('class', 'flow-box-label')
    .attr('x', box1X + boxW / 2)
    .attr('y', boxY + boxH / 2)
    .attr('text-anchor', 'middle')
    .attr('dy', '0.35em')
    .text('Affinity Partners');

  // arrow 2: Affinity Partners -> Phoenix Financial (starts flush against
  // the Affinity Partners box)
  const line2Start = box1X + boxW;
  const arrow2End = box2X - 8;
  svg.append('line')
    .attr('class', 'connector')
    .attr('x1', line2Start)
    .attr('y1', personCY)
    .attr('x2', arrow2End)
    .attr('y2', personCY)
    .attr('marker-end', 'url(#arrowhead)');

  // two fixed lines, bottom-aligned so the last line sits the same
  // `labelGap` above the arrow as the single-line "Founded..." label
  const label2X = (line2Start + arrow2End) / 2;
  const label2 = svg.append('text')
    .attr('class', 'connector-label')
    .attr('text-anchor', 'middle');
  label2.append('tspan').attr('x', label2X).attr('y', personCY - labelGap - 14).text('Which is the largest');
  label2.append('tspan').attr('x', label2X).attr('y', personCY - labelGap).text('shareholder in...');

  // box: Phoenix Financial
  svg.append('rect')
    .attr('class', 'flow-box')
    .attr('x', box2X)
    .attr('y', boxY)
    .attr('width', boxW)
    .attr('height', boxH)
    .attr('rx', 6)
    .attr('fill', BOX_FILL);

  svg.append('text')
    .attr('class', 'flow-box-label')
    .attr('x', box2X + boxW / 2)
    .attr('y', boxY + boxH / 2)
    .attr('text-anchor', 'middle')
    .attr('dy', '0.35em')
    .text('Phoenix Financial');

  // return path: exits Phoenix Financial on its right side, elbows down,
  // then points left into the caption, which is centered on the full width
  const arrowY = returnY;
  const returnExitX = box2X + boxW;
  const returnElbowX = returnExitX + 16;
  const captionCenterX = width / 2;
  // half the width of the longest wrapped line ("including those tied to
  // Israeli military operations."), used so the two-line block (which is
  // right-aligned within itself) reads as centered on the page.
  const captionHalfWidth = 128;
  const captionRightX = captionCenterX + captionHalfWidth;
  const arrowTipX = captionRightX + 10;
  svg.append('path')
    .attr('class', 'connector')
    .attr('d', `M ${returnExitX} ${boxY + boxH / 2} H ${returnElbowX} V ${arrowY} H ${arrowTipX}`)
    .attr('fill', 'none')
    .attr('marker-end', 'url(#arrowhead)');

  const caption = svg.append('text')
    .attr('class', 'connector-label return-label')
    .attr('text-anchor', 'end');

  // vertically centered on the arrow: first line above, second below;
  // both lines share the same right edge (captionRightX)
  caption.append('tspan').attr('x', captionRightX).attr('y', arrowY - 7).text('Which invests in hundreds of companies,');
  caption.append('tspan').attr('x', captionRightX).attr('dy', '1.3em').text('including those tied to Israeli military operations.');
}

function drawFlowDiagramMobile(wrap, width) {
  const centerX = width / 2;
  const personR = 46;
  const photoR = personR - 2;
  const boxW = Math.min(260, width - 40);
  const boxH = 44;
  const arrowLen1 = 56;
  const arrowLen2 = 70;
  const arrowLen3 = 40;

  let cy = 10;

  const svg = wrap.append('svg').attr('class', 'flow-diagram flow-diagram-mobile');

  const defs = svg.append('defs');
  defs.append('marker')
    .attr('id', 'arrowhead-mobile')
    .attr('viewBox', '0 0 10 10')
    .attr('refX', 8)
    .attr('refY', 5)
    .attr('markerWidth', 7)
    .attr('markerHeight', 7)
    .attr('orient', 'auto-start-reverse')
    .append('path')
    .attr('d', 'M 0 0 L 10 5 L 0 10 z')
    .attr('fill', '#b1b1b1');

  // person photo
  const personCY = cy + personR;
  const clipId = 'kushner-clip-mobile';
  defs.append('clipPath')
    .attr('id', clipId)
    .append('circle')
    .attr('cx', centerX)
    .attr('cy', personCY)
    .attr('r', photoR);

  svg.append('circle')
    .attr('cx', centerX)
    .attr('cy', personCY)
    .attr('r', personR)
    .attr('fill', PERSON_BG);

  // rendered a little oversized and shifted down within the fixed circular
  // clip, so the crop sits lower (more shoulders, less headroom) without
  // leaving a gap at the top of the circle
  const imgSize = photoR * 2 * 1.15;
  const imgOffsetY = 6;
  svg.append('image')
    .attr('href', kushnerImg)
    .attr('x', centerX - imgSize / 2)
    .attr('y', personCY - imgSize / 2 + imgOffsetY)
    .attr('width', imgSize)
    .attr('height', imgSize)
    .attr('clip-path', `url(#${clipId})`)
    .attr('preserveAspectRatio', 'xMidYMid slice');

  cy = personCY + personR + 20;
  svg.append('text')
    .attr('class', 'person-name')
    .attr('x', centerX)
    .attr('y', cy)
    .attr('text-anchor', 'middle')
    .text('Jared Kushner');

  // the line breaks to leave a gap for its label, which sits centered
  // directly over the arrow (rather than beside it)
  function downArrow(lines, len) {
    cy += 10;
    const arrowTop = cy;
    const arrowBottom = cy + len;
    const midY = (arrowTop + arrowBottom) / 2;
    const gapHalf = (lines.length === 1 ? 7 : 15);

    svg.append('line')
      .attr('class', 'connector')
      .attr('x1', centerX)
      .attr('y1', arrowTop)
      .attr('x2', centerX)
      .attr('y2', midY - gapHalf);

    svg.append('line')
      .attr('class', 'connector')
      .attr('x1', centerX)
      .attr('y1', midY + gapHalf)
      .attr('x2', centerX)
      .attr('y2', arrowBottom)
      .attr('marker-end', 'url(#arrowhead-mobile)');

    const label = svg.append('text')
      .attr('class', 'connector-label halo')
      .attr('text-anchor', 'middle');

    if (lines.length === 1) {
      label.attr('x', centerX).attr('y', midY).attr('dy', '0.35em').text(lines[0]);
    } else {
      label.append('tspan').attr('x', centerX).attr('y', midY - 7).text(lines[0]);
      label.append('tspan').attr('x', centerX).attr('y', midY + 7).text(lines[1]);
    }

    cy = arrowBottom + 10;
  }

  function drawBox(label) {
    svg.append('rect')
      .attr('class', 'flow-box')
      .attr('x', centerX - boxW / 2)
      .attr('y', cy)
      .attr('width', boxW)
      .attr('height', boxH)
      .attr('rx', 6)
      .attr('fill', BOX_FILL);
    svg.append('text')
      .attr('class', 'flow-box-label')
      .attr('x', centerX)
      .attr('y', cy + boxH / 2)
      .attr('text-anchor', 'middle')
      .attr('dy', '0.35em')
      .text(label);
    cy += boxH;
  }

  downArrow(['Founded...'], arrowLen1);
  drawBox('Affinity Partners');
  downArrow(['Which is the largest', 'shareholder in...'], arrowLen2);
  drawBox('Phoenix Financial');

  // final arrow: straight down, no elbow, into the caption below
  cy += 14;
  svg.append('line')
    .attr('class', 'connector')
    .attr('x1', centerX)
    .attr('y1', cy)
    .attr('x2', centerX)
    .attr('y2', cy + arrowLen3)
    .attr('marker-end', 'url(#arrowhead-mobile)');
  cy += arrowLen3 + 26;

  const caption = svg.append('text')
    .attr('class', 'connector-label return-label')
    .attr('text-anchor', 'middle');
  caption.append('tspan').attr('x', centerX).attr('y', cy).text('Which invests in hundreds of companies,');
  caption.append('tspan').attr('x', centerX).attr('y', cy + 16).text('including those tied to Israeli military operations.');
  cy += 16 + 14;

  svg.attr('viewBox', `0 0 ${width} ${cy}`)
    .attr('width', '100%')
    .attr('height', cy);
}

function drawBar(wrap, isMobile) {
  // desktop splits into the top four companies plus a combined "Other";
  // mobile narrows to Elbit vs. everything else, since five labels don't fit
  // legibly at that width
  const segments = isMobile ? buildSegments(1, 'Others') : buildSegments(4);
  const colors = buildPalette(segments);
  const total = segments.reduce((sum, d) => sum + d.value, 0);
  const lastIndex = segments.length - 1;

  // cumulative left offset of each label, so labels can be pinned to the
  // exact start of their own span regardless of neighboring label widths
  const offsets = [];
  let acc = 0;
  segments.forEach((d) => {
    offsets.push(acc);
    acc += (d.value / total) * 100;
  });

  const barWrap = wrap.append('div').attr('class', 'bar-wrap');

  const bar = barWrap.append('div').attr('class', 'bar');
  bar.selectAll('.segment')
    .data(segments)
    .enter()
    .append('div')
    .attr('class', 'segment')
    .style('width', d => `${(d.value / total) * 100}%`)
    .style('background-color', (d, i) => colors[i]);

  // labels are positioned absolutely (rather than as flex siblings) so the
  // "Other" label can take whatever width it needs, right-anchored, without
  // shifting the others out of alignment with their own bar segment
  const labels = barWrap.append('div').attr('class', 'bar-labels');
  const labelItems = labels.selectAll('.bar-label')
    .data(segments)
    .enter()
    .append('div')
    .attr('class', (d, i) => `bar-label ${i === 0 ? 'is-primary' : 'is-secondary'}`)
    .style('left', (d, i) => (i === lastIndex ? null : `${offsets[i]}%`))
    .style('right', (d, i) => (i === lastIndex ? '0' : null))
    .style('width', (d, i) => (i === lastIndex ? null : `${(d.value / total) * 100}%`));

  labelItems.append('div')
    .attr('class', 'name')
    .text(d => BAR_LABEL_OVERRIDES[d.label] || d.label);

  labelItems.append('div')
    .attr('class', 'value')
    .text(d => formatDollars(d.value));
}

function drawGrid(wrap) {
  const grid = wrap.append('div').attr('class', 'category-grid');

  const columns = grid.selectAll('.category-col')
    .data(CATEGORY_ORDER)
    .enter()
    .append('div')
    .attr('class', 'category-col');

  columns.append('h4').attr('class', 'category-title').text(d => d);

  const companies = columns.selectAll('.company')
    .data(category => data.filter(d => d.category === category))
    .enter()
    .append('div')
    .attr('class', 'company');

  companies.append('div').attr('class', 'company-name').text(d => d.company);
  companies.append('div').attr('class', 'company-value').text(d => formatDollars(d.value));
  companies.append('div').attr('class', 'company-desc').text(d => d.description);
}

function drawGridMobile(wrap) {
  const list = wrap.append('div').attr('class', 'category-list');

  const rows = list.selectAll('.category-row')
    .data(CATEGORY_ORDER)
    .enter()
    .append('div')
    .attr('class', 'category-row');

  rows.append('h4').attr('class', 'category-title').text(d => d);

  // categories stay stacked (one full-width row each), but the companies
  // within a category lay out as a 2-column grid
  const companyGrid = rows.append('div').attr('class', 'company-grid');

  const companies = companyGrid.selectAll('.company')
    .data(category => data.filter(d => d.category === category))
    .enter()
    .append('div')
    .attr('class', 'company');

  companies.append('div').attr('class', 'company-name').text(d => d.company);
  companies.append('div').attr('class', 'company-value').text(d => formatDollars(d.value));
  companies.append('div').attr('class', 'company-desc').text(d => d.description);
}

export default function (props, container, isMobile) {
  const { initialWidth } = props;

  const wrap = container.append('div').attr('class', `kushner-explainer${isMobile ? ' is-mobile' : ''}`);

  if (isMobile) {
    drawFlowDiagramMobile(wrap, initialWidth);
  } else {
    drawFlowDiagram(wrap, initialWidth);
  }

  wrap.append('div')
    .attr('class', 'stat-headline')
    .text(STAT_HEADLINE);

  drawBar(wrap, isMobile);

  wrap.append('hr').attr('class', 'divider');

  if (isMobile) {
    drawGridMobile(wrap);
  } else {
    drawGrid(wrap);
  }
}
